import type { CheckoutState } from './checkout.types';

interface CheckoutSummaryProps {
  checkoutData: CheckoutState;
  validationErrors: string[];
  orderId: string | null;
  isRecovering: boolean;
  isWidgetReady: boolean;
}

export function CheckoutSummary({ 
  checkoutData, 
  validationErrors, 
  orderId, 
  isRecovering, 
  isWidgetReady 
}: CheckoutSummaryProps) {
  const getOrderTypeText = (type: string) => {
    switch (type) {
      case 'DEADLINE_DISCOUNT': return '마감할인';
      case 'RESERVATION_DISCOUNT': return '예약할인';
      case 'REGULAR_ORDER': return '일반주문';
      default: return '';
    }
  };

  return (
    <>
      {/* Store Info */}
      <div className="bg-white mt-2 p-4">
        <h2 className="font-semibold text-lg mb-2">{checkoutData.storeName}</h2>
        <div className="flex items-center gap-2">
          <span className="px-2 py-1 bg-blue-100 text-blue-700 text-sm rounded">
            {getOrderTypeText(checkoutData.orderType)}
          </span>
        </div>
      </div>

      {/* Order Items */}
      <div className="bg-white mt-2 p-4">
        <h3 className="font-semibold mb-3">주문 상품</h3>
        <div className="space-y-3">
          {checkoutData.items.map((item, index) => (
            <div key={index} className="flex justify-between items-start pb-3 border-b last:border-b-0">
              <div className="flex-1">
                <p className="font-medium">{item.productName}</p>
                <p className="text-sm text-gray-600 mt-1">
                  {item.discountedPrice.toLocaleString()}원 × {item.quantity}개
                </p>
              </div>
              <p className="font-semibold">
                {(item.discountedPrice * item.quantity).toLocaleString()}원
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Validation Errors */}
      {validationErrors.length > 0 && (
        <div className="bg-red-50 mx-4 mt-2 p-3 rounded-lg">
          <p className="text-red-700 font-medium mb-2">결제를 진행할 수 없습니다</p>
          <ul className="space-y-1">
            {validationErrors.map((error, index) => (
              <li key={index} className="text-sm text-red-600">• {error}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Payment Widget Container */}
      {!validationErrors.length && orderId && (
        <div className="bg-white mt-2 p-4">
          <h3 className="font-semibold mb-3">결제 수단 선택</h3>
          
          {/* TossPayments Widget이 렌더링될 컨테이너 */}
          <div id="payment-widget-container" className="min-h-[300px]">
            {isRecovering ? (
              <div className="flex items-center justify-center h-[300px]">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-500 mx-auto mb-2"></div>
                  <p className="text-sm text-gray-600">결제 환경을 복구하고 있습니다...</p>
                  <p className="text-xs text-gray-500 mt-1">잠시만 기다려주세요</p>
                </div>
              </div>
            ) : !isWidgetReady && !validationErrors.length ? (
              <div className="flex items-center justify-center h-[300px]">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-2"></div>
                  <p className="text-sm text-gray-600">결제 위젯 로딩 중...</p>
                </div>
              </div>
            ) : null}
          </div>
          
          {/* 약관 동의 위젯 */}
          <div id="payment-widget-container-agreement" className="mt-4"></div>
        </div>
      )}

      {/* Price Summary */}
      <div className="bg-white mt-2 p-4">
        <h3 className="font-semibold mb-3">결제 금액</h3>
        <div className="space-y-2">
          <div className="flex justify-between">
            <span className="text-gray-600">상품 금액</span>
            <span>{checkoutData.totalPrice.toLocaleString()}원</span>
          </div>
          {checkoutData.usedPoints > 0 && (
            <div className="flex justify-between text-red-600">
              <span>포인트 사용</span>
              <span>-{checkoutData.usedPoints.toLocaleString()}원</span>
            </div>
          )}
          <div className="flex justify-between font-semibold text-lg pt-2 border-t">
            <span>최종 결제 금액</span>
            <span className="text-green-600">{checkoutData.finalPrice.toLocaleString()}원</span>
          </div>
        </div>
      </div>
    </>
  );
}